class HeaderCarousel {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
    if (this.slides.length <= 1) return;

    this.prevBtn = wrapper.querySelector('[data-carousel-prev]');
    this.nextBtn = wrapper.querySelector('[data-carousel-next]');
    this.indicatorsEl = wrapper.querySelector('[data-carousel-indicators]');
    this.indicators = [];
    this.currentIndex = 0;

    this.isPointerDown = false;
    this.isDragging = false;
    this.suppressClick = false;
    this.axisLock = null;
    this.startX = 0;
    this.startY = 0;
    this.scrollStart = 0;
    this.startIndex = 0;
    this.pointerId = null;
    this.dragDistance = 0;
    this.dragThreshold = 8;
    this.commitRatio = 0.18;
    this.flickVelocity = 0.35; // px/ms
    this.lastX = 0;
    this.lastTime = 0;
    this.velocityX = 0;
    this.snapRestoreTimer = null;
    this.settleTimer = null;
    this.isHovered = false;
    this.isFocusInside = false;
    this.isSettling = false;

    this.autoplayEnabled = wrapper.dataset.autoplay === 'true';
    this.autoplayInterval = Number(wrapper.dataset.autoplayInterval || 4000);
    this.autoplayTimer = null;

    this.onScroll = this.onScroll.bind(this);
    this.goTo = this.goTo.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.updateSlidesPerView = this.updateSlidesPerView.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.pauseAutoplay = this.pauseAutoplay.bind(this);
    this.resumeAutoplay = this.resumeAutoplay.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.updateSlidesPerView();
    this.bindEvents();
    this.updateUI();
    this.startAutoplay();
  }

  get slidesPerView() {
    const desktop = Number(this.wrapper.dataset.slidesDesktop || 1);
    const mobile = Number(this.wrapper.dataset.slidesMobile || 1);
    return window.matchMedia('(min-width: 750px)').matches ? desktop : mobile;
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - this.slidesPerView);
  }

  get pageCount() {
    return this.maxIndex + 1;
  }

  slideWidth() {
    if (!this.slides.length) return 0;
    return this.slides[0].getBoundingClientRect().width;
  }

  slideStride() {
    if (this.slides.length < 2) return this.slideWidth();
    const stride = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    return stride > 0 ? stride : this.slideWidth();
  }

  bindEvents() {
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('dragstart', this.onDragStart);
    this.track.addEventListener('click', this.onClickCapture, true);
    this.prevBtn?.addEventListener('click', () => {
      this.pauseAutoplay();
      this.prev();
      this.resumeAutoplay();
    });
    this.nextBtn?.addEventListener('click', () => {
      this.pauseAutoplay();
      this.next();
      this.resumeAutoplay();
    });
    this.wrapper.addEventListener('mouseenter', () => {
      // Touch browsers synthesize sticky hover; don't let that kill autoplay.
      if (!window.matchMedia('(hover: hover)').matches) return;
      this.isHovered = true;
      this.pauseAutoplay();
    });
    this.wrapper.addEventListener('mouseleave', () => {
      this.isHovered = false;
      this.resumeAutoplay();
    });
    this.wrapper.addEventListener('focusin', this.onFocusIn);
    this.wrapper.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.updateSlidesPerView);
  }

  canAutoplay() {
    return (
      this.autoplayEnabled &&
      this.pageCount > 1 &&
      !this.isPointerDown &&
      !this.isDragging &&
      !this.isSettling &&
      !this.isHovered &&
      !this.isFocusInside &&
      !document.hidden
    );
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.canAutoplay()) return;

    this.autoplayTimer = window.setInterval(() => {
      if (!this.canAutoplay()) return;
      const wrapping = this.currentIndex >= this.maxIndex;
      const nextIndex = wrapping ? 0 : this.currentIndex + 1;
      // Jump instantly on loop so we don't animate backward through every slide.
      this.goTo(nextIndex, !wrapping);
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() {
    this.stopAutoplay();
  }

  resumeAutoplay() {
    this.startAutoplay();
  }

  onFocusIn() {
    this.isFocusInside = true;
    this.pauseAutoplay();
  }

  onFocusOut(event) {
    if (this.wrapper.contains(event.relatedTarget)) return;
    this.isFocusInside = false;
    this.resumeAutoplay();
  }

  onVisibilityChange() {
    if (document.hidden) this.pauseAutoplay();
    else this.resumeAutoplay();
  }

  rebuildIndicators() {
    if (!this.indicatorsEl) return;

    const count = this.pageCount;

    if (count <= 1) {
      this.indicatorsEl.hidden = true;
      this.indicatorsEl.innerHTML = '';
      this.indicators = [];
      this.stopAutoplay();
      return;
    }

    this.indicatorsEl.hidden = false;

    if (this.indicators.length === count) return;

    this.indicatorsEl.innerHTML = '';
    this.indicators = [];

    for (let i = 0; i < count; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'header-carousel__indicator';
      button.setAttribute('data-carousel-indicator', '');
      button.setAttribute('aria-label', `Go to slide ${i + 1}`);
      button.addEventListener('click', () => {
        this.pauseAutoplay();
        this.goTo(i);
        this.resumeAutoplay();
      });
      this.indicatorsEl.appendChild(button);
      this.indicators.push(button);
    }

    this.startAutoplay();
  }

  onDragStart(event) {
    event.preventDefault();
  }

  clearPointerListeners() {
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerUp);
  }

  disableSnap() {
    if (this.snapRestoreTimer) {
      window.clearTimeout(this.snapRestoreTimer);
      this.snapRestoreTimer = null;
    }
    this.track.style.scrollBehavior = 'auto';
    this.track.style.scrollSnapType = 'none';
  }

  restoreSnap(delay = 0) {
    if (this.snapRestoreTimer) {
      window.clearTimeout(this.snapRestoreTimer);
      this.snapRestoreTimer = null;
    }

    const apply = () => {
      // Re-enable snap first while behavior is still forced to auto,
      // so any correction doesn't animate/jerk.
      this.track.style.scrollSnapType = '';
      this.snapRestoreTimer = window.setTimeout(() => {
        this.track.style.scrollBehavior = '';
        this.snapRestoreTimer = null;
      }, 50);
    };

    if (delay > 0) {
      this.snapRestoreTimer = window.setTimeout(apply, delay);
    } else {
      apply();
    }
  }

  settleTo(index, { smooth = false } = {}) {
    if (this.settleTimer) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }

    this.isSettling = true;
    this.disableSnap();
    this.currentIndex = Math.min(Math.max(index, 0), this.maxIndex);
    const stride = this.slideStride();
    if (stride) {
      if (smooth) {
        this.track.scrollTo({
          left: this.currentIndex * stride,
          behavior: 'smooth',
        });
      } else {
        this.track.scrollLeft = this.currentIndex * stride;
      }
    }
    this.updateUI();

    const finish = () => {
      this.settleTimer = null;
      this.restoreSnap();
      this.isSettling = false;
      this.isDragging = false;
      this.isPointerDown = false;
      this.resumeAutoplay();
    };

    if (smooth) {
      // Keep snap off until the ease finishes so it doesn't fight the animation.
      this.settleTimer = window.setTimeout(finish, 350);
    } else {
      requestAnimationFrame(finish);
    }
  }

  resetPointerState({ resume = true } = {}) {
    this.isPointerDown = false;
    this.isDragging = false;
    this.suppressClick = false;
    this.axisLock = null;
    this.dragDistance = 0;
    this.velocityX = 0;
    this.pointerId = null;
    this.track.classList.remove('is-grabbing', 'is-dragging');
    this.restoreSnap();
    this.clearPointerListeners();
    if (resume) this.resumeAutoplay();
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pauseAutoplay();
    this.isSettling = false;
    if (this.settleTimer) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }

    // Touch/pen: let native overflow + scroll-snap handle swipes.
    // Chrome Android ignores late preventDefault, so JS drag is unreliable there.
    if (event.pointerType !== 'mouse') {
      this.isPointerDown = true;
      this.isDragging = false;
      this.suppressClick = false;
      this.axisLock = null;
      this.pointerId = event.pointerId;
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerUp);
      return;
    }

    const stride = this.slideStride();

    this.isPointerDown = true;
    this.isDragging = false;
    this.suppressClick = false;
    this.axisLock = null;
    this.dragDistance = 0;
    this.velocityX = 0;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.lastX = event.clientX;
    this.lastTime = performance.now();
    this.scrollStart = this.track.scrollLeft;
    this.startIndex = stride
      ? Math.min(Math.max(Math.round(this.scrollStart / stride), 0), this.maxIndex)
      : this.currentIndex;

    // Avoid killing link clicks; still block native drag on non-interactive areas.
    if (!event.target.closest('a, button')) {
      event.preventDefault();
    }

    this.track.classList.add('is-grabbing');

    document.addEventListener('pointermove', this.onPointerMove, { passive: false });
    document.addEventListener('pointerup', this.onPointerUp);
    document.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    const now = performance.now();
    const dt = now - this.lastTime;

    if (dt > 0) {
      this.velocityX = (event.clientX - this.lastX) / dt;
    }
    this.lastX = event.clientX;
    this.lastTime = now;

    if (!this.axisLock) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < this.dragThreshold && absY < this.dragThreshold) return;

      // Prefer horizontal when close so diagonal swipes still navigate.
      if (absX >= absY) {
        this.axisLock = 'x';
        this.isDragging = true;
        this.suppressClick = true;
        this.track.classList.add('is-dragging');
        this.disableSnap();
      } else {
        // Clear vertical intent: drop our handlers so page scroll continues.
        this.resetPointerState({ resume: true });
        return;
      }
    }

    if (this.axisLock !== 'x') return;

    event.preventDefault();
    this.dragDistance = Math.abs(deltaX);
    this.track.scrollLeft = this.scrollStart - deltaX;
  }

  resolveDragTarget(clientX) {
    const stride = this.slideStride();
    if (!stride) return this.startIndex;

    const dragDelta = this.startX - clientX; // >0 = finger moved left = next
    const distance = Math.abs(dragDelta);
    const flicked = Math.abs(this.velocityX) >= this.flickVelocity;
    const passed = distance >= stride * this.commitRatio;

    let target = this.startIndex;

    if (passed || flicked) {
      const goingNext = flicked ? this.velocityX < 0 : dragDelta > 0;
      target = goingNext ? this.startIndex + 1 : this.startIndex - 1;
    }

    return Math.min(Math.max(target, 0), this.maxIndex);
  }

  syncIndexFromScroll() {
    const stride = this.slideStride();
    if (!stride) return;
    const index = Math.min(
      Math.max(Math.round(this.track.scrollLeft / stride), 0),
      this.maxIndex
    );
    if (index !== this.currentIndex) {
      this.currentIndex = index;
      this.updateUI();
    }
  }

  onPointerUp(event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

    // Touch/pen native swipe — sync after snap settles, then resume autoplay.
    if (event.pointerType !== 'mouse') {
      this.isPointerDown = false;
      this.isHovered = false;
      this.pointerId = null;
      this.clearPointerListeners();

      window.setTimeout(() => {
        this.syncIndexFromScroll();
        this.resumeAutoplay();
      }, 50);
      return;
    }

    const wasDragging = this.isDragging && this.axisLock === 'x';
    const clientX = event.clientX;

    this.isPointerDown = false;
    this.track.classList.remove('is-grabbing', 'is-dragging');
    this.clearPointerListeners();

    // Keep suppressClick through the click that follows a swipe.
    const finishGesture = () => {
      this.suppressClick = false;
      this.dragDistance = 0;
      this.velocityX = 0;
      this.axisLock = null;
      this.pointerId = null;
    };

    if (wasDragging) {
      // Clear drag lock before settle resumes autoplay (rAF can beat setTimeout(0)).
      this.isDragging = false;
      this.settleTo(this.resolveDragTarget(clientX), { smooth: true });
      window.setTimeout(finishGesture, 0);
      return;
    }

    this.restoreSnap();
    window.setTimeout(() => {
      this.isDragging = false;
      finishGesture();
      this.resumeAutoplay();
    }, 0);
  }

  onClickCapture(event) {
    if (this.suppressClick || this.isDragging || this.dragDistance > this.dragThreshold) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  updateSlidesPerView() {
    this.wrapper.style.setProperty('--carousel-slides-mobile', this.wrapper.dataset.slidesMobile || 1);
    this.wrapper.style.setProperty('--carousel-slides-desktop', this.wrapper.dataset.slidesDesktop || 1);
    this.wrapper.style.setProperty('--carousel-gap-mobile', `${this.wrapper.dataset.gapMobile || 0}px`);
    this.wrapper.style.setProperty('--carousel-gap-desktop', `${this.wrapper.dataset.gapDesktop || 0}px`);
    this.rebuildIndicators();
    this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
    this.goTo(this.currentIndex, false);
  }

  onScroll() {
    if (this.isPointerDown) return;
    const stride = this.slideStride();
    if (!stride) return;
    const index = Math.round(this.track.scrollLeft / stride);
    if (index !== this.currentIndex) {
      this.currentIndex = Math.min(Math.max(index, 0), this.maxIndex);
      this.updateUI();
    }
  }

  goTo(index, smooth = true) {
    this.currentIndex = Math.min(Math.max(index, 0), this.maxIndex);
    const stride = this.slideStride();
    this.track.scrollTo({
      left: this.currentIndex * stride,
      behavior: smooth ? 'smooth' : 'auto',
    });
    this.updateUI();
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  updateUI() {
    this.indicators.forEach((indicator, index) => {
      indicator.classList.toggle('active', index === this.currentIndex);
      indicator.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });

    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-header-carousel]').forEach((wrapper) => {
    new HeaderCarousel(wrapper);
  });
});
